/**
 * BulkActionBar Component
 * 
 * Action toolbar for multi-select document operations
 */

import React, { useState } from 'react';
import { 
  X, FolderInput, Download, Trash2, Share2, 
  CheckSquare, AlertTriangle
} from 'lucide-react';
import { cn } from '@/core/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onMove: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onShare: () => void;
  isMoving?: boolean;
  isDownloading?: boolean;
  isDeleting?: boolean;
  isSharing?: boolean;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onClearSelection,
  onMove,
  onDownload,
  onDelete,
  onShare,
  isMoving = false,
  isDownloading = false,
  isDeleting = false,
  isSharing = false,
  className,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAnyLoading = isMoving || isDownloading || isDeleting || isSharing;

  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete();
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <div className={cn(
      'fixed bottom-6 left-1/2 -translate-x-1/2 z-40',
      'bg-navy text-white rounded-xl shadow-lg',
      'px-4 py-3 flex items-center gap-4',
      'animate-in slide-in-from-bottom-4 duration-200',
      className
    )}>
      {/* Selection Count */}
      <div className="flex items-center gap-2 pr-4 border-r border-white/20">
        <CheckSquare className="w-5 h-5 text-sage-light" />
        <span className="font-medium">
          {selectedCount} selected
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {/* Move */}
        <button
          onClick={onMove}
          disabled={isAnyLoading}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
            'hover:bg-white/10',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          title="Move to folder"
        >
          <FolderInput className="w-4 h-4" />
          <span className="hidden sm:inline">
            {isMoving ? 'Moving...' : 'Move'}
          </span>
        </button>

        {/* Download */}
        <button
          onClick={onDownload}
          disabled={isAnyLoading}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
            'hover:bg-white/10',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          title="Download as ZIP"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">
            {isDownloading ? 'Preparing...' : 'Download'}
          </span>
        </button>

        {/* Share */}
        <button
          onClick={onShare}
          disabled={isAnyLoading}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
            'hover:bg-white/10',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          title="Share with attorney"
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">
            {isSharing ? 'Sharing...' : 'Share'}
          </span>
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-white/20 mx-1" />

        {/* Delete */}
        {showDeleteConfirm ? (
          <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-300" />
            <span className="text-sm">Delete {selectedCount}?</span>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-2 py-1 bg-red-500 rounded text-sm hover:bg-red-600"
            >
              {isDeleting ? 'Deleting...' : 'Yes'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-2 py-1 text-sm hover:text-white/80"
            >
              No
            </button>
          </div>
        ) : (
          <button
            onClick={handleDelete}
            disabled={isAnyLoading}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
              'hover:bg-red-500/20 text-red-300',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Delete selected"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        )}
      </div>

      {/* Close */}
      <button
        onClick={onClearSelection}
        className={cn(
          'ml-2 p-2 rounded-lg transition-colors',
          'hover:bg-white/10'
        )}
        title="Clear selection"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export default BulkActionBar;
